import Phaser from 'phaser';
import { GameEntity } from '../socket/SocketManager';
import { IsoMap } from './IsoMap';

function darkenColor(color: number, factor: number): number {
  const r = Math.floor(((color >> 16) & 0xff) * factor);
  const g = Math.floor(((color >> 8) & 0xff) * factor);
  const b = Math.floor((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

export class IsoVehicle {
  private graphics: Phaser.GameObjects.Graphics;
  private currentX: number;
  private currentY: number;
  private previousX: number;
  private previousY: number;
  private targetX: number;
  private targetY: number;
  private entityType: string;
  private direction: string = 'E';
  private state: string = 'driving';
  private color: number;

  constructor(
    private scene: Phaser.Scene,
    entity: GameEntity,
    private isoMap: IsoMap,
  ) {
    this.graphics = scene.add.graphics();
    this.entityType = entity.type;
    this.color = entity.color || (entity.type === 'POLICE_CAR' ? 0x3a6bc4 : 0xdddddd);
    this.direction = entity.direction || 'E';
    this.state = entity.state || 'driving';

    const pos = isoMap.gridToIso(entity.gridX, entity.gridY);
    this.currentX = pos.x;
    this.currentY = pos.y;
    this.previousX = pos.x;
    this.previousY = pos.y;
    this.targetX = pos.x;
    this.targetY = pos.y;

    this.draw();
  }

  updateTarget(entity: GameEntity): void {
    this.previousX = this.currentX;
    this.previousY = this.currentY;
    this.direction = entity.direction || this.direction;
    this.state = entity.state || this.state;
    this.color = entity.color || this.color;

    const pos = this.isoMap.gridToIso(entity.gridX, entity.gridY);
    this.targetX = pos.x;
    this.targetY = pos.y;
  }

  interpolate(t: number): void {
    if (this.state === 'waiting') {
      // No interpolation when waiting
      this.draw();
      return;
    }

    // Smooth deceleration when stopping
    let adjustedT = t;
    if (this.state === 'stopping') {
      adjustedT = t * (2 - t); // Ease-out for deceleration feel
    }

    this.currentX = this.previousX + (this.targetX - this.previousX) * adjustedT;
    this.currentY = this.previousY + (this.targetY - this.previousY) * adjustedT;
    this.draw();
  }

  private draw(): void {
    this.graphics.clear();
    const cx = this.currentX;
    const cy = this.currentY;

    const approxGrid = cy / 16 + cx / 32;
    this.graphics.setDepth(approxGrid);

    const bodyColor = this.color;
    const darkColor = darkenColor(bodyColor, 0.7);

    // Rotate car shape based on direction
    const isNS = this.direction === 'N' || this.direction === 'S';
    const bw = isNS ? 8 : 14;
    const bh = isNS ? 14 : 8;

    // Car body (isometric)
    this.graphics.fillStyle(bodyColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, cy - bh);
    this.graphics.lineTo(cx + bw, cy - 2);
    this.graphics.lineTo(cx, cy + bh - 4);
    this.graphics.lineTo(cx - bw, cy - 2);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Roof (smaller, raised)
    this.graphics.fillStyle(darkColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(cx, cy - bh - 4);
    this.graphics.lineTo(cx + bw * 0.6, cy - 5);
    this.graphics.lineTo(cx, cy - 2);
    this.graphics.lineTo(cx - bw * 0.6, cy - 5);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Headlights based on direction
    if (this.state === 'driving' || this.state === 'turning') {
      this.graphics.fillStyle(0xffffcc, 0.7);
      switch (this.direction) {
        case 'N':
          this.graphics.fillCircle(cx - 3, cy - bh + 1, 1.5);
          this.graphics.fillCircle(cx + 3, cy - bh + 1, 1.5);
          break;
        case 'S':
          this.graphics.fillCircle(cx - 3, cy + bh - 5, 1.5);
          this.graphics.fillCircle(cx + 3, cy + bh - 5, 1.5);
          break;
        case 'E':
          this.graphics.fillCircle(cx + bw - 1, cy - 3, 1.5);
          this.graphics.fillCircle(cx + bw - 1, cy, 1.5);
          break;
        case 'W':
          this.graphics.fillCircle(cx - bw + 1, cy - 3, 1.5);
          this.graphics.fillCircle(cx - bw + 1, cy, 1.5);
          break;
      }
    }

    // Brake lights when stopping
    if (this.state === 'stopping' || this.state === 'waiting') {
      this.graphics.fillStyle(0xff0000, 0.8);
      switch (this.direction) {
        case 'N':
          this.graphics.fillCircle(cx - 3, cy + bh - 5, 1.5);
          this.graphics.fillCircle(cx + 3, cy + bh - 5, 1.5);
          break;
        case 'S':
          this.graphics.fillCircle(cx - 3, cy - bh + 1, 1.5);
          this.graphics.fillCircle(cx + 3, cy - bh + 1, 1.5);
          break;
        case 'E':
          this.graphics.fillCircle(cx - bw + 1, cy - 1, 1.5);
          this.graphics.fillCircle(cx - bw + 1, cy + 2, 1.5);
          break;
        case 'W':
          this.graphics.fillCircle(cx + bw - 1, cy - 1, 1.5);
          this.graphics.fillCircle(cx + bw - 1, cy + 2, 1.5);
          break;
      }
    }

    // Police lights
    if (this.entityType === 'POLICE_CAR') {
      const flashPhase = Math.floor(Date.now() / 250) % 4;
      const leftColor = flashPhase < 2 ? 0xff0000 : 0x0000ff;
      const rightColor = flashPhase < 2 ? 0x0000ff : 0xff0000;

      this.graphics.fillStyle(leftColor, 0.9);
      this.graphics.fillCircle(cx - 3, cy - bh - 5, 2);
      this.graphics.fillStyle(rightColor, 0.9);
      this.graphics.fillCircle(cx + 3, cy - bh - 5, 2);
    }

    // Shadow
    this.graphics.fillStyle(0x000000, 0.15);
    this.graphics.fillEllipse(cx, cy + bh - 2, bw * 2, bh * 0.6);
  }

  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
